'use client'

import React, { useMemo, useState } from 'react'
import { Button } from '@workspace/ui/components/Button'
import { Popover, PopoverTrigger } from '@workspace/ui/components/Popover'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { Checkbox } from '@workspace/ui/components/Checkbox'
import { CirclePlus, XIcon } from 'lucide-react'
import { Spinner } from '@workspace/ui/components/Spinner'
import { useTranslation } from 'react-i18next'
import { cn } from '@workspace/ui/lib/utils'
import { Placement } from 'react-aria'
import { removeVietnameseTones } from '@/shared/utils/index.utils'

export interface FilterOption {
    id: string
    name: string
}

export interface FilterDropdownProps {
    placeholder: string
    options: FilterOption[]
    selectedValues: string[]
    onChange: (values: string[]) => void
    onSearchChange?: (search: string) => void
    onLoadMore?: () => void
    hasNextPage?: boolean
    isFetchingNextPage?: boolean
    className?: string
    containerClassName?: string
    iconClassName?: string
    inputPlaceholder?: string
    placement?: Placement
    icon?: React.ComponentType<{ className?: string }>
    showSelectedItems?: boolean
    setOpen?: (open: boolean) => void
    isLoading?: boolean
    emptyText?: string
    isDisabled?: boolean
    disabledTooltip?: string
}

export const FilterDropdown = React.memo(
    ({
        placeholder,
        options,
        selectedValues,
        onChange,
        onSearchChange,
        onLoadMore,
        hasNextPage,
        isFetchingNextPage,
        inputPlaceholder,
        className,
        containerClassName,
        iconClassName,
        placement,
        icon,
        showSelectedItems,
        setOpen,
        isLoading,
        emptyText,
        isDisabled,
        disabledTooltip,
    }: FilterDropdownProps) => {
        const { t } = useTranslation()
        const [searchQuery, setSearchQuery] = useState('')

        const filteredOptions = onSearchChange
            ? options
            : options.filter(opt => removeVietnameseTones(opt.name).includes(removeVietnameseTones(searchQuery.trim())))

        const handleSearchChange = (val: string) => {
            setSearchQuery(val)
            onSearchChange?.(val)
        }

        const handleToggle = (id: string) => {
            const newValues = selectedValues.includes(id)
                ? selectedValues.filter(v => v !== id)
                : [...selectedValues, id]
            onChange(newValues)
        }

        const hasSelection = useMemo(() => selectedValues.length > 0, [selectedValues])
        const selectedOptions = useMemo(
            () => options.filter(opt => selectedValues.includes(opt.id)),
            [options, selectedValues],
        )

        const handleScroll = (e: React.UIEvent<HTMLElement>) => {
            const bottom =
                Math.abs(e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight) < 10
            if (bottom && onLoadMore && !isLoading && !isFetchingNextPage && hasNextPage) {
                onLoadMore()
            }
        }

        return (
            <div className={cn('flex flex-col gap-2', containerClassName)}>
                <div
                    className={cn('relative', isDisabled && 'cursor-not-allowed')}
                    title={isDisabled ? disabledTooltip : undefined}
                >
                    <PopoverTrigger onOpenChange={setOpen}>
                        <Button
                            variant="outline"
                            isDisabled={isDisabled}
                            className={cn(
                                'relative flex items-center gap-2 justify-start h-10 px-3 text-sm font-normal bg-background rounded-lg hover:bg-secondary text-foreground',
                                isDisabled && 'pointer-events-none',
                                className,
                            )}
                        >
                            {React.createElement(icon || CirclePlus, {
                                className: `w-4.5! h-4.5! text-foreground ${iconClassName}`,
                            })}
                            {placeholder}
                            {hasSelection && (
                                <>
                                    <span className="w-px h-4 bg-border" />
                                    <span className="text-xs font-medium text-white rounded-sm bg-primary px-1.5 py-0.5">
                                        {selectedValues.length}
                                    </span>
                                </>
                            )}
                        </Button>
                        <Popover
                            className="w-[220px] flex flex-col gap-0 rounded-lg shadow-m bg-background overflow-hidden"
                            placement={placement || 'bottom start'}
                        >
                            <div className="pt-1.5">
                                <BsSearchField
                                    autoTrim
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    autoFocus
                                    className="ring-0! shadow-none border-b rounded-none px-3.5 shrink-0"
                                    placeholder={t(inputPlaceholder || 'common.search')}
                                />
                            </div>
                            <div
                                className="overflow-y-auto max-h-72 flex flex-col py-1.5 gap-1"
                                onScroll={handleScroll}
                            >
                                {isLoading && !isFetchingNextPage ? (
                                    <div className="flex items-center justify-center gap-2 py-6 px-3.5 text-sm text-muted-foreground">
                                        <Spinner className="size-4 text-muted-foreground" />
                                        {t('common.loading')}
                                    </div>
                                ) : filteredOptions.length > 0 ? (
                                    <>
                                        {filteredOptions.map(option => (
                                            <Checkbox
                                                key={option.id}
                                                value={option.id}
                                                isSelected={selectedValues.includes(option.id)}
                                                onChange={() => handleToggle(option.id)}
                                                className="flex items-center gap-2 px-3.5 py-1.5 text-sm font-normal"
                                            >
                                                {option.name}
                                            </Checkbox>
                                        ))}
                                        {isFetchingNextPage && (
                                            <div className="flex items-center justify-center py-2 shrink-0">
                                                <Spinner className="size-4 text-primary" />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="flex items-center justify-center py-6 px-3.5 text-sm text-muted-foreground">
                                        {emptyText || t('common.emptyState')}
                                    </div>
                                )}
                            </div>
                        </Popover>
                    </PopoverTrigger>

                    {hasSelection && (
                        <Button
                            variant="destructive"
                            size="iconSm"
                            isDisabled={isDisabled}
                            onClick={() => onChange([])}
                            className="absolute -top-2 -right-2 rounded-full size-4!"
                            aria-label={t('common.clear')}
                        >
                            <XIcon className="size-3! text-white" />
                        </Button>
                    )}
                </div>

                {showSelectedItems && hasSelection && (
                    <div className="flex flex-col gap-1">
                        {selectedOptions.map(option => (
                            <div
                                key={option.id}
                                className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm bg-secondary rounded-md"
                            >
                                <span>{option.name}</span>
                                <Button
                                    variant="ghost"
                                    size="iconSm"
                                    onClick={() => onChange(selectedValues.filter(id => id !== option.id))}
                                    className="size-4!"
                                >
                                    <XIcon className="size-3!" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    },
)
